import React, { useEffect, useRef, useState, useCallback } from 'react';

interface LrcLine { time: number; text: string; }

const parseLRC = (lrc: string): LrcLine[] => {
  const lines: LrcLine[] = [];
  lrc.split('\n').forEach(line => {
    const match = line.match(/\[(\d{2}):(\d{2})\.(\d{2,3})\](.*)/);
    if (match) {
      const time = parseInt(match[1])*60 + parseInt(match[2]) + parseInt(match[3].padEnd(3,'0'))/1000;
      if (match[4].trim()) lines.push({ time, text: match[4].trim() });
    }
  });
  return lines.sort((a,b) => a.time-b.time);
};

interface Props {
  song: any;
  streamToken: string;
  apiUrl: string;
  onFinished: () => void;
}

export default function KaraokePlayer({ song, streamToken, apiUrl, onFinished }: Props) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [lrcLines, setLrcLines] = useState<LrcLine[]>([]);
  const [currentLine, setCurrentLine] = useState(-1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const lyricsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch(`${apiUrl}/songs/stream/${streamToken}/lyrics`)
      .then(async res => {
        const fmt = res.headers.get('X-Lyrics-Format');
        if (fmt === 'lrc') setLrcLines(parseLRC(await res.text()));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [streamToken, apiUrl]);

  useEffect(() => {
    if (currentLine >= 0 && lyricsRef.current) {
      lyricsRef.current.querySelector(`[data-line="${currentLine}"]`)?.scrollIntoView({ behavior:'smooth', block:'center' });
    }
  }, [currentLine]);

  const handleTimeUpdate = useCallback(() => {
    const t = audioRef.current?.currentTime || 0;
    setCurrentTime(t);
    let cur = -1;
    for (let i = 0; i < lrcLines.length; i++) {
      if (lrcLines[i].time <= t) cur = i; else break;
    }
    setCurrentLine(cur);
  }, [lrcLines]);

  const togglePlay = () => {
    if (playing) { audioRef.current?.pause(); setPlaying(false); }
    else { audioRef.current?.play(); setPlaying(true); }
  };

  const pad = (n: number) => String(Math.floor(n)).padStart(2,'0');
  const fmt = (s: number) => `${pad(s/60)}:${pad(s%60)}`;
  const progress = duration > 0 ? (currentTime/duration)*100 : 0;

  if (loading) return (
    <div style={{ height:'100vh', background:'#0a0a1a', display:'flex', alignItems:'center', justifyContent:'center', color:'#a78bfa', fontFamily:'sans-serif', fontSize:'1.2rem' }}>
      Cargando...
    </div>
  );

  return (
    <div style={{ height:'100vh', background:'#0a0a1a', display:'flex', flexDirection:'column', fontFamily:'sans-serif', overflow:'hidden' }}>
      <div style={{ padding:'1rem 1.5rem 0.5rem', textAlign:'center' }}>
        <div style={{ fontSize:'1.4rem', fontWeight:800, color:'#fff' }}>{song.title}</div>
        <div style={{ color:'#a78bfa', fontSize:'0.9rem' }}>{song.artist}</div>
      </div>

      <div ref={lyricsRef} style={{ flex:1, overflow:'hidden', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'1rem', gap:'0.5rem' }}>
        {lrcLines.length === 0 ? (
          <div style={{ fontSize:'3rem', color:'#333' }}>🎤</div>
        ) : lrcLines.map((line, i) => {
          const isCur = i === currentLine;
          const isNear = Math.abs(i-currentLine) <= 1;
          return (
            <div key={i} data-line={i} style={{ textAlign:'center', color: isCur ? '#f59e0b' : '#fff', fontWeight: isCur ? 800 : 500, fontSize: isCur ? '1.8rem' : isNear ? '1.3rem' : '1rem', opacity: Math.abs(i-currentLine) > 2 ? 0.2 : 1, transition:'all 0.3s', padding:'0.1rem 0', textShadow: isCur ? '0 0 20px rgba(245,158,11,0.5)' : 'none' }}>
              {line.text}
            </div>
          );
        })}
      </div>

      <div style={{ padding:'1rem 1.5rem 2rem', background:'rgba(0,0,0,0.5)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'0.75rem' }}>
          <span style={{ color:'#888', fontSize:'0.75rem', fontFamily:'monospace' }}>{fmt(currentTime)}</span>
          <div style={{ flex:1, height:'4px', background:'#2a2a3e', borderRadius:'99px', cursor:'pointer' }} onClick={e => {
            const r = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
            if (audioRef.current) audioRef.current.currentTime = ((e.clientX-r.left)/r.width)*duration;
          }}>
            <div style={{ height:'100%', background:'#7C3AED', borderRadius:'99px', width:`${progress}%`, transition:'width 0.5s linear' }} />
          </div>
          <span style={{ color:'#888', fontSize:'0.75rem', fontFamily:'monospace' }}>{fmt(duration)}</span>
        </div>
        <div style={{ display:'flex', gap:'0.75rem', justifyContent:'center' }}>
          <button onClick={togglePlay} style={{ width:'60px', height:'60px', borderRadius:'50%', background:'#7C3AED', border:'none', color:'#fff', fontSize:'1.5rem', cursor:'pointer' }}>
            {playing ? '⏸' : '▶'}
          </button>
          <button onClick={onFinished} style={{ padding:'0 1.5rem', height:'60px', borderRadius:'10px', background:'#2a2a3e', border:'1px solid #444', color:'#fff', cursor:'pointer' }}>
            ← Volver
          </button>
        </div>
      </div>

      <audio ref={audioRef} src={`${apiUrl}/songs/stream/${streamToken}/mp3`} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={e => setDuration((e.target as HTMLAudioElement).duration)} onEnded={onFinished} preload="auto" style={{ display:'none' }} />
    </div>
  );
}
